import { forwardRef, HTMLAttributes, ReactElement, useContext } from 'react';

import { ListSubheader, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import {
    AutoCompleteOuterElementContext,
    AutocompleteOuterElementContextProvider,
    statusColors,
    useResetCache,
} from '..';

const LISTBOX_PADDING = 8; // px

const renderRow = (props: ListChildComponentProps) => {
    const { data, index, style } = props;
    const dataSet = data[index];
    const inlineStyle = {
        ...style,
        top: (style.top as number) + LISTBOX_PADDING,
    };

    if (dataSet.hasOwnProperty('group')) {
        return (
            <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
                {dataSet.group}
            </ListSubheader>
        );
    }

    const option = dataSet[1];
    return (
        <Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
            {option.label}
            {option.status && <span style={statusColors[option.status]}>{option.status}</span>}
        </Typography>
    );
};

const OuterElementType = forwardRef<HTMLDivElement>((props, ref) => {
    const outerProps = useContext(AutoCompleteOuterElementContext);
    return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListBox = forwardRef<HTMLDivElement, HTMLAttributes<HTMLElement>>(function ListBox(
    props,
    ref
) {
    const { children, ...other } = props;
    const itemData: ReactElement[] = [];

    (children as ReactElement[]).forEach((item: ReactElement & { children?: ReactElement[] }) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
    });

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
        noSsr: true,
    });

    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child: ReactElement) => {
        if (child.hasOwnProperty('group')) {
            return 48;
        }

        return itemSize;
    };

    const getHeight = () => {
        if (itemCount > 8) {
            return 8 * itemSize;
        }

        return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
        <AutocompleteOuterElementContextProvider value={other} divRef={ref}>
            <VariableSizeList
                itemData={itemData}
                height={getHeight() + 2 * LISTBOX_PADDING}
                width="100%"
                ref={gridRef}
                outerElementType={OuterElementType}
                innerElementType="ul"
                itemSize={(index) => getChildSize(itemData[index])}
                overscanCount={5}
                itemCount={itemCount}
            >
                {renderRow}
            </VariableSizeList>
        </AutocompleteOuterElementContextProvider>
    );
});

export default ListBox;
